import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Button } from "./Button";
import { cx } from "./cx";
import { Chevron } from "./Popover";
import { useControllableOpen, useDismiss, useFloating, type OpenControl } from "./hooks";

export interface ComboOption {
  value: string;
  label: ReactNode;
  /** 右侧等宽显示 ID 或代码。 */
  hint?: ReactNode;
  /** 第二行说明。 */
  desc?: ReactNode;
  disabled?: boolean;
  /** 搜索用的文本，默认取 label 为字符串时的值。 */
  search?: string;
}

interface BaseProps extends OpenControl {
  options: ComboOption[];
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  size?: "sm";
  width?: number | string;
  block?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  maxTags?: number;
  /** 同一种下拉出现多行时区分它们的说明，例如"第 2 个成员"（#110）。读屏念作"说明 + 当前值"，不会盖掉选中的值。 */
  ariaLabel?: string;
  /** 关联的说明或错误提示的 id，写到触发元素的 aria-describedby；invalid 时触发元素显示红框。 */
  describedBy?: string;
  className?: string;
}

export interface SingleProps extends BaseProps { multiple?: false; value: string | null; onChange: (v: string) => void }
export interface MultiProps extends BaseProps { multiple: true; value: string[]; onChange: (v: string[]) => void }
export type ComboboxProps = SingleProps | MultiProps;

function Tick({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M2.5 6.5l2.5 2.5 4.5-5" /></svg>
  );
}

function searchText(o: ComboOption): string {
  return (o.search ?? [o.label, o.hint, o.desc].filter((x) => typeof x === "string").join(" ")).toLowerCase();
}

/** 可搜索的单选或多选。少于 8 个固定选项用 Select。 */
export function Combobox(props: ComboboxProps) {
  const { options, placeholder, searchable = true, searchPlaceholder = "搜索", size, width, block, disabled, invalid, maxTags = 2, ariaLabel, describedBy, className } = props;
  const uid = useId();
  const multiple = props.multiple === true;
  const [open, setOpen] = useControllableOpen(props);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const close = useCallback(() => { setOpen(false); setQuery(""); }, [setOpen]);
  useDismiss(ref, open, close);
  const floating = useFloating(ref, open, "start", close);
  useEffect(() => { if (open) (inputRef.current ?? listRef.current)?.focus({ preventScroll: true }); }, [open]); // 不让聚焦去滚容器

  const values: string[] = multiple ? (props.value as string[]) : props.value == null ? [] : [props.value as string];
  const filtered = query ? options.filter((o) => searchText(o).includes(query.toLowerCase())) : options;

  useEffect(() => {
    let idx = 0;
    if (!query && !multiple) { const i = filtered.findIndex((o) => o.value === values[0]); if (i >= 0) idx = i; }
    setActive(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);
  useEffect(() => { (listRef.current?.children[active] as HTMLElement | undefined)?.scrollIntoView?.({ block: "nearest" }); }, [active]);

  const isSelected = (v: string) => values.includes(v);
  const select = (o: ComboOption) => {
    if (o.disabled) return;
    if (props.multiple === true) {
      const next = isSelected(o.value) ? values.filter((x) => x !== o.value) : [...values, o.value];
      props.onChange(next);
    } else {
      props.onChange(o.value);
      close();
    }
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[active]) select(filtered[active]); }
    else if (e.key === "Backspace" && props.multiple === true && !query && values.length) props.onChange(values.slice(0, -1));
  };
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;

  let content: ReactNode;
  if (props.multiple === true) {
    const shown = values.slice(0, maxTags);
    content = values.length ? (
      <>
        {shown.map((v) => { const label = labelOf(v); return (
          <span key={v} className="ax-combobox-tag">
            {/* 小号（工具栏）只占一行，标签放不下时截断、悬停看全称 */}
            <span className="ax-combobox-tag-text" title={typeof label === "string" ? label : undefined}>{label}</span>
            <button type="button" className="x" aria-label="移除" onClick={(e) => { e.stopPropagation(); props.onChange(values.filter((x) => x !== v)); }}>×</button>
          </span>
        ); })}
        {values.length > shown.length && <span className="ax-combobox-more">+{values.length - shown.length}</span>}
      </>
    ) : (
      <span className="placeholder">{placeholder}</span>
    );
  } else {
    const selected = options.find((o) => o.value === props.value);
    // 单选的值只占一行，放不下截断、悬停看全称：工具栏里的控件高度要一致
    content = selected ? <span className="ax-combobox-value" title={typeof selected.label === "string" ? selected.label : undefined}>{selected.label}</span> : <span className="placeholder">{placeholder}</span>;
  }

  return (
    <div className={cx("ax-combobox", block && "block", className)} ref={ref} style={width ? { width } : undefined}>
      {/* 触发器不是 <button>：多选时里面有真正的"移除"按钮，按钮不能嵌套按钮。 */}
      {/* 名称由说明与触发器自身的内容拼成：直接用 aria-label 会把当前值从名称里挤掉。被引用的隐藏元素仍参与名称计算 */}
      {ariaLabel && <span id={`${uid}l`} hidden>{ariaLabel}</span>}
      <div role="button" id={ariaLabel ? `${uid}t` : undefined} aria-labelledby={ariaLabel ? `${uid}l ${uid}t` : undefined} tabIndex={disabled ? -1 : 0} className={cx("ax-combobox-trigger", size === "sm" && "sm")} aria-disabled={disabled || undefined} aria-expanded={open} aria-haspopup="listbox" aria-invalid={invalid || undefined} aria-describedby={describedBy}
        onClick={() => { if (!disabled) setOpen(!open); }}
        onKeyDown={(e) => { if (disabled) return; if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") { e.preventDefault(); setOpen(true); } }}>
        {content}
        <Chevron />
      </div>
      {open && (
        <div className="ax-combobox-panel" style={floating}>
          {searchable && <input ref={inputRef} className="ax-input ax-input-sm" placeholder={searchPlaceholder} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKey} aria-label="搜索选项" />}
          <ul className="ax-listbox" role="listbox" aria-multiselectable={multiple || undefined} ref={listRef} tabIndex={searchable ? undefined : 0} onKeyDown={searchable ? undefined : onKey}>
            {filtered.length ? (
              filtered.map((o, i) => (
                <li key={o.value} role="option" aria-selected={isSelected(o.value)} aria-disabled={o.disabled || undefined} className={cx("ax-option", i === active && "active")} onMouseEnter={() => setActive(i)} onClick={() => select(o)}>
                  <Tick className="check" />
                  {/* 名称只占一行，放不下截断、悬停看全称；hint 不折行，窄的触发器里两边不会各自断行错位（#240） */}
                  <span className="ax-option-label">
                    <span className="ax-option-text" title={typeof o.label === "string" ? o.label : undefined}>{o.label}</span>
                    {o.desc && <div className="desc">{o.desc}</div>}
                  </span>
                  {o.hint != null && <span className="hint">{o.hint}</span>}
                </li>
              ))
            ) : (
              <li className="ax-listbox-empty">没有匹配的选项</li>
            )}
          </ul>
          {props.multiple === true && (
            <div className="ax-combobox-foot">
              <span>已选 {values.length} 项</span>
              <Button variant="ghost" size="sm" onClick={() => props.onChange([])}>清空</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export type SelectProps = Omit<SingleProps, "multiple" | "searchable" | "searchPlaceholder" | "maxTags">;

/** 自绘的单选下拉，是 Combobox 去掉搜索框的形态。 */
export function Select(props: SelectProps) {
  return <Combobox {...props} multiple={false} searchable={false} />;
}
